import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Search, RefreshCw, Trophy, Clock, Eye, Lightbulb, CheckCircle2, AlertTriangle, List } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MIN_GRID_SIZE = 10;
const MAX_GRID_SIZE = 22; // Increased to support longer words like NOREPINEPHRINE (14 chars)

// Calculate optimal grid size based on words
function calculateGridSize(words) {
  const maxWordLength = Math.max(...words.map(w => w.length));
  // Grid should be at least maxWordLength + 2 to allow for placement flexibility
  const minRequired = maxWordLength + 2;
  return Math.min(MAX_GRID_SIZE, Math.max(MIN_GRID_SIZE, minRequired));
}

// Validate answer for Word Search
function validateAnswer(answer) {
  if (!answer || typeof answer !== 'string') {
    return { valid: false, error: 'Missing or invalid correct_answer', cleanAnswer: '' };
  }
  
  const cleanAnswer = answer.toUpperCase().replace(/[^A-Z]/g, '');
  
  if (cleanAnswer.length === 0) {
    return { valid: false, error: 'correct_answer contains no letters', cleanAnswer: '' };
  }
  
  if (cleanAnswer.length < 2) {
    return { valid: false, error: 'correct_answer must have at least 2 letters', cleanAnswer: '' };
  }
  
  // Allow up to 20 characters (matches backend validation)
  if (cleanAnswer.length > 20) {
    return { valid: false, error: `correct_answer too long (${cleanAnswer.length} chars, max 20)`, cleanAnswer };
  }
  
  return { valid: true, cleanAnswer };
}

// Generate word search grid with MULTIPLE target words
function generateMultiWordGrid(words, gridSize) {
  const grid = Array(gridSize).fill(null).map(() => Array(gridSize).fill(''));
  const placements = [];
  const directions = [
    [0, 1],   // right
    [1, 0],   // down
    [1, 1],   // diagonal down-right
    [0, -1],  // left
    [-1, 0],  // up
    [-1, -1], // diagonal up-left
    [1, -1],  // diagonal down-left
    [-1, 1],  // diagonal up-right
  ];
  
  // Sort words by length (longest first) for better placement
  const sortedWords = [...words].sort((a, b) => b.length - a.length);
  
  for (const word of sortedWords) {
    let placed = false;
    
    // Try multiple attempts to place each word
    for (let attempt = 0; attempt < 200 && !placed; attempt++) {
      const dir = directions[Math.floor(Math.random() * directions.length)];
      
      let maxRow = gridSize - 1;
      let minRow = 0;
      let maxCol = gridSize - 1;
      let minCol = 0;
      
      if (dir[0] > 0) maxRow = gridSize - word.length;
      if (dir[0] < 0) minRow = word.length - 1;
      if (dir[1] > 0) maxCol = gridSize - word.length;
      if (dir[1] < 0) minCol = word.length - 1;
      
      if (maxRow < minRow || maxCol < minCol) continue;
      
      const startRow = minRow + Math.floor(Math.random() * (maxRow - minRow + 1));
      const startCol = minCol + Math.floor(Math.random() * (maxCol - minCol + 1));
      
      // Check if word can be placed (no conflicts)
      let canPlace = true;
      for (let i = 0; i < word.length && canPlace; i++) {
        const r = startRow + dir[0] * i;
        const c = startCol + dir[1] * i;
        if (r < 0 || r >= gridSize || c < 0 || c >= gridSize) {
          canPlace = false;
        } else if (grid[r][c] !== '' && grid[r][c] !== word[i]) {
          canPlace = false;
        }
      }
      
      if (canPlace) {
        const cells = [];
        for (let i = 0; i < word.length; i++) {
          const r = startRow + dir[0] * i;
          const c = startCol + dir[1] * i;
          grid[r][c] = word[i];
          cells.push({ row: r, col: c });
        }
        placements.push({ word, cells, direction: dir });
        placed = true;
      }
    }
    
    if (!placed) {
      // If a word couldn't be placed, return partial success
      console.warn(`Could not place word: ${word}`);
    }
  }
  
  // Fill empty cells with random letters
  const letters = 'AAAAABCCDDEEEEFGHIIJKLMNOOOOPPQRRSSTTUUVWXYZ';
  for (let r = 0; r < gridSize; r++) {
    for (let c = 0; c < gridSize; c++) {
      if (grid[r][c] === '') {
        grid[r][c] = letters[Math.floor(Math.random() * letters.length)];
      }
    }
  }
  
  return { grid, placements, gridSize };
}

export default function WordSearch() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const topics = searchParams.get('topics') || '';
  const difficulties = searchParams.get('difficulties') || '';
  const subtopics = searchParams.get('subtopics') || '';

  const [questions, setQuestions] = useState([]);
  const [puzzleQuestions, setPuzzleQuestions] = useState([]);  // Questions in current puzzle
  const [grid, setGrid] = useState([]);
  const [gridSize, setGridSize] = useState(MIN_GRID_SIZE);
  const [placements, setPlacements] = useState([]);
  const [gridError, setGridError] = useState(null);
  const [foundWords, setFoundWords] = useState(new Set());
  const [revealedWords, setRevealedWords] = useState(new Set());
  const [selectedCells, setSelectedCells] = useState([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [startTime, setStartTime] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [showRationale, setShowRationale] = useState(null);  // question_id to show rationale
  
  // Stats tracking
  const [totalCompleted, setTotalCompleted] = useState(0);
  const [totalRevealed, setTotalRevealed] = useState(0);
  const [totalTime, setTotalTime] = useState(0);
  const [sessionSaved, setSessionSaved] = useState(false);
  const [puzzleIndex, setPuzzleIndex] = useState(0);

  const fetchQuestions = useCallback(async () => {
    try {
      const params = {
        game_type: 'word_search',
        topics: topics || undefined,
        difficulties: difficulties || undefined,
        subtopics: subtopics || undefined,
        limit: 20
      };
      const res = await axios.get(`${BACKEND_URL}/api/games/questions`, { params, withCredentials: true });
      const gameQuestions = res.data.questions || [];
      
      if (gameQuestions.length === 0) {
        toast.error('No word search questions available');
        setLoading(false);
        return;
      }
      
      setQuestions(gameQuestions);
      initializePuzzle(gameQuestions, 0);
    } catch (err) {
      toast.error('Failed to load game');
    } finally {
      setLoading(false);
    }
  }, [topics, difficulties, subtopics]);

  // Initialize puzzle with multiple questions (up to 5 per puzzle)
  // Now includes deduplication of correct_answer values
  const initializePuzzle = (allQuestions, startIdx) => {
    // Take questions for this puzzle, filtering duplicates by normalized correct_answer
    const seenAnswers = new Set();
    const uniqueQuestions = [];
    
    for (let i = startIdx; i < allQuestions.length && uniqueQuestions.length < 5; i++) {
      const q = allQuestions[i];
      // Normalize for comparison: trim, uppercase, remove non-letters
      const normalizedAnswer = (q.correct_answer || '').trim().toUpperCase().replace(/[^A-Z]/g, '');
      
      if (normalizedAnswer && !seenAnswers.has(normalizedAnswer)) {
        seenAnswers.add(normalizedAnswer);
        uniqueQuestions.push(q);
      }
    }
    
    if (uniqueQuestions.length === 0) {
      setGridError('No more unique questions available');
      return;
    }
    
    setPuzzleQuestions(uniqueQuestions);
    
    // Validate and clean all answers
    const validWords = [];
    const invalidQuestions = [];
    
    for (const q of uniqueQuestions) {
      const validation = validateAnswer(q.correct_answer);
      if (validation.valid) {
        validWords.push(validation.cleanAnswer);
      } else {
        invalidQuestions.push({ question: q, error: validation.error });
      }
    }
    
    if (validWords.length === 0) {
      setGrid([]);
      setPlacements([]);
      setGridError('No valid answers to display in puzzle');
      setFoundWords(new Set());
      setRevealedWords(new Set());
      setStartTime(Date.now());
      setElapsed(0);
      return;
    }
    
    // Calculate grid size based on longest word
    const optimalSize = calculateGridSize(validWords);
    setGridSize(optimalSize);
    
    // Generate grid with all valid words
    const result = generateMultiWordGrid(validWords, optimalSize);
    
    if (!result.grid || result.placements.length === 0) {
      setGrid([]);
      setPlacements([]);
      setGridError('Could not generate puzzle grid');
      setFoundWords(new Set());
      setRevealedWords(new Set());
      setStartTime(Date.now());
      setElapsed(0);
      return;
    }
    
    setGrid(result.grid);
    setPlacements(result.placements);
    setGridError(invalidQuestions.length > 0 ? `${invalidQuestions.length} question(s) had invalid answers` : null);
    setFoundWords(new Set());
    setRevealedWords(new Set());
    setShowRationale(null);
    setStartTime(Date.now());
    setElapsed(0);
  };

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  useEffect(() => {
    const allDone = puzzleQuestions.length > 0 && 
      (foundWords.size + revealedWords.size) >= placements.length;
    
    if (startTime && !allDone) {
      const timer = setInterval(() => setElapsed(Math.floor((Date.now() - startTime) / 1000)), 1000);
      return () => clearInterval(timer);
    }
  }, [startTime, foundWords.size, revealedWords.size, placements.length, puzzleQuestions.length]);

  // Save session
  const saveGameSession = useCallback(async () => {
    if (sessionSaved) return;
    try {
      await axios.post(`${BACKEND_URL}/api/games/session`, {
        game_type: 'wordsearch',
        topic_key: topics || null,
        difficulty: difficulties || null,
        completed_items: totalCompleted,
        revealed_items: totalRevealed,
        total_items: totalCompleted + totalRevealed,
        correct_items: totalCompleted,
        time_seconds: totalTime
      }, { withCredentials: true });
      setSessionSaved(true);
    } catch (err) {
      console.error('Failed to save session');
    }
  }, [topics, difficulties, totalCompleted, totalRevealed, totalTime, sessionSaved]);

  const handleCellMouseDown = (row, col) => {
    if (isSelecting) return;
    setIsSelecting(true);
    setSelectedCells([{ row, col }]);
  };

  const handleCellMouseEnter = (row, col) => {
    if (!isSelecting) return;
    
    if (selectedCells.length === 0) {
      setSelectedCells([{ row, col }]);
      return;
    }
    
    const start = selectedCells[0];
    const dr = row - start.row;
    const dc = col - start.col;
    
    // Only allow straight lines
    const isValidLine = (dr === 0 || dc === 0 || Math.abs(dr) === Math.abs(dc));
    if (!isValidLine) return;
    
    const steps = Math.max(Math.abs(dr), Math.abs(dc));
    if (steps === 0) {
      setSelectedCells([start]);
      return;
    }
    
    const stepR = dr / steps;
    const stepC = dc / steps;
    
    const cells = [];
    for (let i = 0; i <= steps; i++) {
      cells.push({ 
        row: start.row + Math.round(stepR * i), 
        col: start.col + Math.round(stepC * i) 
      });
    }
    setSelectedCells(cells);
  };

  const handleMouseUp = () => {
    if (!isSelecting) return;
    setIsSelecting(false);
    
    const selectedWord = selectedCells.map(c => grid[c.row]?.[c.col] || '').join('');
    
    // Check if selection matches any placement
    for (const placement of placements) {
      if (foundWords.has(placement.word) || revealedWords.has(placement.word)) continue;
      
      const targetWord = placement.word;
      const reversed = targetWord.split('').reverse().join('');
      
      if (selectedWord === targetWord || selectedWord === reversed) {
        const sortedSelected = [...selectedCells].sort((a, b) => a.row - b.row || a.col - b.col);
        const sortedPlacement = [...placement.cells].sort((a, b) => a.row - b.row || a.col - b.col);
        
        if (sortedSelected.length === sortedPlacement.length &&
            sortedSelected.every((c, i) => c.row === sortedPlacement[i].row && c.col === sortedPlacement[i].col)) {
          // Found a word!
          setFoundWords(prev => new Set([...prev, placement.word]));
          setTotalCompleted(c => c + 1);
          
          // Find the question for this word
          const q = puzzleQuestions.find(q => 
            q.correct_answer.toUpperCase().replace(/[^A-Z]/g, '') === placement.word
          );
          if (q) {
            setShowRationale(q.question_id);
          }
          break;
        }
      }
    }
    
    setSelectedCells([]);
  };

  const handleShowAnswer = (word) => {
    if (foundWords.has(word) || revealedWords.has(word)) return;
    
    setRevealedWords(prev => new Set([...prev, word]));
    setTotalRevealed(c => c + 1);
    
    // Find the question for this word
    const q = puzzleQuestions.find(q => 
      q.correct_answer.toUpperCase().replace(/[^A-Z]/g, '') === word
    );
    if (q) {
      setShowRationale(q.question_id);
    }
  };

  const handleNextPuzzle = () => {
    setTotalTime(t => t + elapsed);
    // Find next start index by counting unique answers used so far
    const usedAnswers = new Set();
    let nextIdx = 0;
    for (let i = 0; i < questions.length; i++) {
      const normalizedAnswer = (questions[i].correct_answer || '').trim().toUpperCase().replace(/[^A-Z]/g, '');
      if (!usedAnswers.has(normalizedAnswer)) {
        usedAnswers.add(normalizedAnswer);
        if (usedAnswers.size > puzzleIndex + puzzleQuestions.length) {
          nextIdx = i;
          break;
        }
      }
    }
    
    if (nextIdx < questions.length) {
      setPuzzleIndex(nextIdx);
      initializePuzzle(questions, nextIdx);
    }
  };

  const handleNewSet = () => {
    const shuffled = [...questions].sort(() => Math.random() - 0.5);
    setQuestions(shuffled);
    setPuzzleIndex(0);
    setTotalCompleted(0);
    setTotalRevealed(0);
    setTotalTime(0);
    setSessionSaved(false);
    initializePuzzle(shuffled, 0);
  };

  const isCellSelected = (row, col) => selectedCells.some(c => c.row === row && c.col === col);
  
  const getCellState = (row, col) => {
    for (const p of placements) {
      if (p.cells.some(c => c.row === row && c.col === col)) {
        if (foundWords.has(p.word)) return 'found';
        if (revealedWords.has(p.word)) return 'revealed';
      }
    }
    return 'normal';
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  const puzzleComplete = placements.length > 0 && (foundWords.size + revealedWords.size) >= placements.length;
  const hasMorePuzzles = puzzleIndex + puzzleQuestions.length < questions.length;
  const allComplete = puzzleComplete && !hasMorePuzzles;

  // Save on all complete
  useEffect(() => {
    if (allComplete && !sessionSaved) {
      setTotalTime(t => t + elapsed);
      saveGameSession();
    }
  }, [allComplete, sessionSaved, elapsed, saveGameSession]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">No Word Search Questions</h2>
          <p className="text-slate-600 mb-4">No published word search game questions available</p>
          <Button onClick={() => navigate('/games/wordsearch/setup')}>Change Filters</Button>
        </div>
      </div>
    );
  }

  // Calculate tile size based on grid size - smaller text for larger grids
  const tileSize = gridSize <= 12 ? 'text-sm' : 
                   gridSize <= 14 ? 'text-xs' : 
                   gridSize <= 18 ? 'text-[10px]' : 'text-[8px]';

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-background">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button onClick={() => navigate('/games/wordsearch/setup')} variant="ghost" className="text-slate-600">
              <ArrowLeft className="w-4 h-4 mr-2" /> Exit
            </Button>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 text-slate-600">
                <Clock className="w-4 h-4" />
                <span className="font-mono">{formatTime(elapsed)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Search className="w-5 h-5 text-emerald-500" />
                <span className="font-semibold text-slate-900">Word Search</span>
              </div>
            </div>
            <Button onClick={handleNewSet} variant="ghost" className="text-slate-600">
              <RefreshCw className="w-4 h-4 mr-2" /> New Set
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Word Search Grid */}
          <div className="lg:col-span-2">
            {grid.length > 0 ? (
              <>
                <div 
                  className="bg-white rounded-xl border border-slate-200 p-3 select-none"
                  onMouseLeave={() => { if (isSelecting) { setIsSelecting(false); setSelectedCells([]); } }}
                  onMouseUp={handleMouseUp}
                >
                  <div 
                    className="grid gap-0.5" 
                    style={{ gridTemplateColumns: `repeat(${gridSize}, minmax(0, 1fr))` }}
                  >
                    {grid.map((row, r) => row.map((cell, c) => {
                      const state = getCellState(r, c);
                      const isSelected = isCellSelected(r, c);
                      
                      return (
                        <div
                          key={`${r}-${c}`}
                          onMouseDown={() => handleCellMouseDown(r, c)}
                          onMouseEnter={() => handleCellMouseEnter(r, c)}
                          className={`aspect-square flex items-center justify-center ${tileSize} font-bold rounded cursor-pointer transition-all ${
                            state === 'found' ? 'bg-emerald-500 text-white' :
                            state === 'revealed' ? 'bg-amber-400 text-white' :
                            isSelected ? 'bg-emerald-200 text-emerald-800' :
                            'bg-slate-100 text-slate-700 hover:bg-emerald-100'
                          }`}
                        >
                          {cell}
                        </div>
                      );
                    }))}
                  </div>
                </div>
                <p className="text-sm text-slate-500 mt-2 text-center">
                  Click and drag to select words • Grid: {gridSize}×{gridSize}
                </p>
              </>
            ) : (
              <div className="bg-amber-50 border border-amber-300 rounded-xl p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600 shrink-0" />
                  <div>
                    <h3 className="font-semibold text-amber-800 mb-2">Puzzle Could Not Be Generated</h3>
                    <p className="text-sm text-amber-700">{gridError || 'Unknown error'}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Questions Panel */}
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex items-center gap-2 mb-3">
                <List className="w-4 h-4 text-emerald-600" />
                <h3 className="font-semibold text-slate-900">Find the Answers</h3>
              </div>
              
              <div className="space-y-3">
                {puzzleQuestions.map((q, idx) => {
                  const cleanAnswer = q.correct_answer.toUpperCase().replace(/[^A-Z]/g, '');
                  const isFound = foundWords.has(cleanAnswer);
                  const isRevealed = revealedWords.has(cleanAnswer);
                  const placement = placements.find(p => p.word === cleanAnswer);
                  const isInGrid = !!placement;
                  
                  return (
                    <div 
                      key={q.question_id}
                      className={`p-3 rounded-lg border transition-all ${
                        isFound ? 'bg-emerald-50 border-emerald-200' :
                        isRevealed ? 'bg-amber-50 border-amber-200' :
                        'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                          isFound ? 'bg-emerald-500' :
                          isRevealed ? 'bg-amber-500' :
                          'bg-slate-300'
                        }`}>
                          {isFound ? <CheckCircle2 className="w-3 h-3 text-white" /> :
                           isRevealed ? <Eye className="w-3 h-3 text-white" /> :
                           <span className="text-xs text-white font-medium">{idx + 1}</span>}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${isFound || isRevealed ? 'text-slate-500' : 'text-slate-900'}`}>
                            {q.prompt}
                          </p>
                          
                          {(isFound || isRevealed) && (
                            <p className={`text-xs font-medium mt-1 ${isFound ? 'text-emerald-600' : 'text-amber-600'}`}>
                              {cleanAnswer}
                            </p>
                          )}
                          
                          {!isFound && !isRevealed && isInGrid && (
                            <button
                              onClick={() => handleShowAnswer(cleanAnswer)}
                              className="text-xs text-amber-600 hover:text-amber-700 mt-1 flex items-center gap-1"
                            >
                              <Eye className="w-3 h-3" /> Show Answer
                            </button>
                          )}
                          
                          {!isInGrid && (
                            <p className="text-xs text-red-500 mt-1">Could not place in grid</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Rationale Display */}
            {showRationale && (
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                <div className="flex items-center gap-2 mb-2">
                  <Lightbulb className="w-4 h-4 text-amber-500" />
                  <p className="text-xs uppercase tracking-wide text-slate-600 font-semibold">Rationale</p>
                  <button 
                    onClick={() => setShowRationale(null)}
                    className="ml-auto text-slate-400 hover:text-slate-600 text-xs"
                  >
                    Dismiss
                  </button>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed">
                  {puzzleQuestions.find(q => q.question_id === showRationale)?.rationale}
                </p>
              </div>
            )}

            {/* Progress */}
            <div className="bg-white rounded-xl p-4 border border-slate-200">
              <p className="text-xs uppercase tracking-wide text-slate-500 mb-2">Progress</p>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-600">Found</span>
                  <span className="font-semibold text-emerald-600">{foundWords.size}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Revealed</span>
                  <span className="font-semibold text-amber-600">{revealedWords.size}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-600">Total in Puzzle</span>
                  <span className="font-semibold text-slate-900">{placements.length}</span>
                </div>
                <div className="h-2 bg-slate-100 rounded-full mt-2">
                  <div 
                    className="h-full bg-emerald-500 rounded-full transition-all"
                    style={{ width: `${((foundWords.size + revealedWords.size) / Math.max(placements.length, 1)) * 100}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Next/Complete Button */}
            {puzzleComplete && (
              <div>
                {hasMorePuzzles ? (
                  <Button 
                    onClick={handleNextPuzzle} 
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                  >
                    Next Puzzle
                  </Button>
                ) : (
                  <Button 
                    onClick={() => navigate('/games')} 
                    className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                  >
                    Finish <Trophy className="w-4 h-4 ml-1" />
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* All Complete Celebration */}
        {allComplete && (
          <div className="mt-8 bg-white rounded-xl border border-slate-200 p-8 text-center">
            <Trophy className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Session Complete!</h3>
            <div className="space-y-1 text-slate-600 mb-4">
              <p>Found: <span className="text-emerald-600 font-semibold">{totalCompleted}</span></p>
              <p>Revealed: <span className="text-amber-600 font-semibold">{totalRevealed}</span></p>
              <p>Total time: {formatTime(totalTime + elapsed)}</p>
            </div>
            <div className="flex gap-3 justify-center">
              <Button onClick={handleNewSet} variant="outline">New Set</Button>
              <Button onClick={() => navigate('/games')} className="bg-emerald-500 text-white">Back to Games</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
